import React, { useState, useEffect } from "react";
import { Button, Form, Input, InputNumber, Select, Table, message, Typography } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { API, graphqlOperation } from "aws-amplify";
import { errorToString } from "../../utils";
import { onPremServerInfoFromSystemConfigs, TOUCHSTONE_PLATFORMS } from "../../utils/touchstone";
import Service from "../../services";
import * as mutations from "../../graphql/mutations";

const { Title } = Typography;

const validateNonNegativeInteger = (s) => {
  if (parseInt(s) !== s || s < 0) {
    throw Error("value must be a non-negative integer");
  }
};

const validateNotEmpty = (s) => {
  if (s === "") {
    throw Error("field cannot be empty");
  }
};

const SYSTEM_CONFIG_SPECS = [
  {
    category: /touchstone/,
    keyname: /runpod_max_running_tasks/,
    inputElement: InputNumber,
    validator: validateNonNegativeInteger,
    inputProps: { style: { width: 290 } },
  },
  {
    category: /touchstone/,
    keyname: /default_platform/,
    isDefaultPlatformSpec: true,
  },
  {
    category: /touchstone/,
    keyname: /requirements_spec/,
    inputProps: { style: { width: 290 } },
    inputElement: Input,
  },
  {
    category: /touchstone-servers/,
    keyname: /.*\.description/,
    inputElement: Input,
    validator: validateNotEmpty,
  },
  {
    category: /touchstone-servers/,
    keyname: /.*\.gpu_type/,
    inputElement: Input,
    validator: validateNotEmpty,
  },
  {
    category: /touchstone-servers/,
    keyname: /.*\.num_slots/,
    inputElement: InputNumber,
    validator: validateNonNegativeInteger,
    inputProps: { style: { width: "100%" } },
  },
  {
    category: /touchstone-servers/,
    keyname: /.*\.cpu_gb_per_slot/,
    inputElement: InputNumber,
    validator: validateNonNegativeInteger,
    inputProps: { style: { width: "100%" } },
  },
];

const SYSTEM_CONFIG_DEFAULTS = {
  touchstone: {
    runpod_max_running_tasks: {
      defaultValue: "0",
    },
    default_platform: {
      defaultValue: TOUCHSTONE_PLATFORMS[0].key,
    },
    requirements_spec: {
      defaultValue: "ml-feature-set==0.1.30 ml-data-processor==0.2.3",
    },
  },
};

const getMatchingSpec = (category, keyname) => {
  const spec = SYSTEM_CONFIG_SPECS.filter((s) => category.match(s.category) && keyname.match(s.keyname));
  return spec.length > 0 ? spec[0] : null;
};

const ConfigItem = ({ idx, category, keyname, configs, setConfigs }) => {
  const [form] = Form.useForm();

  const handleUpdate = async () => {
    let params;
    try {
      params = await form.validateFields();
    } catch (error) {
      return;
    }
    try {
      const spec = getMatchingSpec(category, keyname);
      if (spec && spec.validator) {
        spec.validator(params.newValue);
      }

      const input = {
        category,
        keyname,
        value: params.newValue,
      };
      await API.graphql(
        graphqlOperation(mutations.updateSystemConfig, {
          input: input,
        })
      );
      const updatedConfigs = [...configs];
      updatedConfigs[idx].value = params.newValue;
      setConfigs(updatedConfigs);
      message.success("Configuration update successful");
    } catch (error) {
      message.error(errorToString(error));
    }
  };

  const spec = getMatchingSpec(category, keyname);
  let inputElement, inputProps, initialValue;
  if (!spec) {
    inputElement = Input;
    inputProps = {};
    initialValue = configs[idx].value;
  } else if (spec.isDefaultPlatformSpec) {
    // Create the default-platform dropdown
    inputElement = Select;
    const onPremServerInfo = onPremServerInfoFromSystemConfigs(configs);
    const choices = onPremServerInfo.names + TOUCHSTONE_PLATFORMS.map((p) => p.key);
    initialValue = configs[idx].value;
    initialValue = choices.includes(initialValue) ? initialValue : TOUCHSTONE_PLATFORMS[0].key;
    inputProps = {
      options: [
        {
          label: "Platforms",
          title: "platforms",
          options: TOUCHSTONE_PLATFORMS.map((p) => ({ label: p.description, value: p.key })),
        },
        {
          label: "On-prem servers",
          title: "servers",
          options: onPremServerInfo.names.map((p) => ({ label: onPremServerInfo.attributes[p].description, value: p })),
        },
      ],
      style: { minWidth: 290 },
    };
  } else {
    inputElement = spec.inputElement;
    inputProps = spec.inputProps || {};
    initialValue = configs[idx].value;
  }
  return (
    <Form layout="inline" form={form}>
      <Form.Item name="newValue" preserve={false} initialValue={initialValue}>
        {React.createElement(inputElement, { ...inputProps }, null)}
      </Form.Item>
      <Form.Item>
        <Button type="link" icon={<SaveOutlined />} onClick={() => handleUpdate()}>
          Update
        </Button>
      </Form.Item>
    </Form>
  );
};

const SystemTab = () => {
  const [configs, setConfigs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const dbConfigs = await Service.listSystemConfigs();
        const configs = await initConfigs(dbConfigs);
        setConfigs(configs.map((item, idx) => ({ ...item, key: idx })));
      } catch (error) {
        message.error(errorToString(error));
      }
    })();
  }, []);

  // For any system config items that do not exist in the DB, initialize them with
  // a hard-coded value if available
  const initConfigs = async (dbConfigs) => {
    const includesPair = (array, [p1, p2]) => array.some(([a1, a2]) => a1 === p1 && a2 === p2);
    const foundItems = dbConfigs.map((item) => [item.category, item.keyname]);
    const missingItems = [];
    for (const category in SYSTEM_CONFIG_DEFAULTS) {
      const expectedItems = Object.keys(SYSTEM_CONFIG_DEFAULTS[category]).map((keyname) => [category, keyname]);
      missingItems.push(...expectedItems.filter((item) => !includesPair(foundItems, item)));
    }

    const allConfigs = [...dbConfigs];
    await Promise.all(
      missingItems.map(async ([category, keyname]) => {
        const input = { category, keyname, value: SYSTEM_CONFIG_DEFAULTS[category][keyname].defaultValue };
        console.log("Initializing missing SystemConfig value: " + JSON.stringify(input));
        allConfigs.push(input);
        await API.graphql(graphqlOperation(mutations.updateSystemConfig, { input: input }));
      })
    );
    return allConfigs;
  };

  if (!configs) return null;

  const columns = [
    {
      title: "Key Name",
      dataIndex: "keyname",
      key: "keyname",
      sorter: (a, b) => a.keyname.localeCompare(b.keyname),
    },
    {
      title: "Current value",
      dataIndex: "value",
      key: "value",
    },
    {
      title: "New value",
      key: "actions",
      render: (text, item) => (
        <ConfigItem idx={item.key} category={item.category} keyname={item.keyname} configs={configs} setConfigs={setConfigs} />
      ),
    },
  ];

  const categories = [...new Set(configs.map((c) => c.category))];
  categories.sort();
  return (
    <div>
      <Title level={3} style={{ marginTop: 40 }}>
        System Configuration
      </Title>
      {categories.map((category) => (
        <div key={category}>
          <Title level={5}>{category}</Title>
          <Table
            columns={columns}
            dataSource={configs.filter((c) => c.category === category)}
            style={{ marginTop: 20 }}
            pagination={{ position: ["bottomRight"], defaultPageSize: 100 }}
          />
        </div>
      ))}
    </div>
  );
};

export default SystemTab;
